import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Button, Chip, FormControl, InputLabel, Link, MenuItem,
  Paper, Select, TextField, Typography
} from "@material-ui/core";
import { Alert, Pagination, Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import RefreshIcon from "@material-ui/icons/Refresh";
import api from "../../../services/api";
import { AuthContext } from "../../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: "flex", alignItems: "center", flexWrap: "wrap",
    gap: theme.spacing(1.5), margin: theme.spacing(2, 0)
  },
  field: {
    minWidth: 210,
    [theme.breakpoints.down("xs")]: { width: "100%", minWidth: 0 }
  },
  summary: {
    display: "flex", flexWrap: "wrap", gap: theme.spacing(1, 4),
    padding: theme.spacing(2), marginBottom: theme.spacing(3),
    fontVariantNumeric: "tabular-nums"
  },
  section: { marginBottom: theme.spacing(3) },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1) },
  order: {
    padding: theme.spacing(2),
    "& + &": { borderTop: `1px solid ${theme.palette.divider}` }
  },
  orderHeader: {
    display: "flex", alignItems: "baseline", flexWrap: "wrap",
    gap: theme.spacing(1), marginBottom: theme.spacing(0.5)
  },
  client: { fontWeight: 600, overflowWrap: "anywhere" },
  attention: {
    color: theme.palette.type === "dark" ? theme.palette.warning.light : "#795000",
    fontWeight: 600
  },
  prose: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  updates: {
    margin: theme.spacing(1.5, 0, 0), padding: 0, listStyle: "none",
    "& li + li": { marginTop: theme.spacing(1.5) }
  },
  update: { display: "flex", alignItems: "baseline", gap: theme.spacing(1.5) },
  time: { flexShrink: 0, fontVariantNumeric: "tabular-nums" },
  empty: { padding: theme.spacing(3) },
  pagination: { display: "flex", justifyContent: "center", margin: theme.spacing(2, 0) }
}));

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Sao_Paulo" });
const validDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
const dateLabel = value => (value || "").slice(0, 10).split("-").reverse().join("/");
const timeLabel = value => (value || "").slice(11, 16);
const message = error => error?.response?.data?.error || error?.response?.data?.message || "Não foi possível carregar o relatório. Tente novamente.";

const OrderSection = ({ title, orders, attention = false }) => {
  const classes = useStyles();
  if (!orders.length) return null;
  return (
    <section className={classes.section} aria-label={title}>
      <Typography variant="h6" className={classes.sectionTitle}>{title}</Typography>
      <Paper variant="outlined">
        {orders.map(order => (
          <article className={classes.order} key={order.id}>
            <div className={classes.orderHeader}>
              <Link component={RouterLink} to={`/ordens-servico/${order.id}`} aria-label={`Abrir OS ${order.id}`}>
                OS #{order.id}
              </Link>
              <Typography component="span" className={classes.client}>{order.client}</Typography>
              <Chip size="small" variant="outlined" className={attention ? classes.attention : undefined} label={order.status || "Sem status"} />
              {!order.created_on_date && <Typography variant="caption" color="textSecondary">OS de {dateLabel(order.created_at)}</Typography>}
            </div>
            <Typography variant="body2" className={classes.prose}>{order.description || "Sem descrição curta."}</Typography>
            {order.updates.length ? (
              <ul className={classes.updates} aria-label={`Andamentos da OS ${order.id}`}>
                {order.updates.map(update => (
                  <li key={update.id} className={classes.update}>
                    <Typography component="span" variant="caption" color="textSecondary" className={classes.time}>{timeLabel(update.timestamp)}</Typography>
                    <Typography variant="body2" className={classes.prose}>{update.description}</Typography>
                  </li>
                ))}
              </ul>
            ) : <Box mt={1}><Typography variant="caption" color="textSecondary">Sem andamentos registrados nesta data.</Typography></Box>}
          </article>
        ))}
      </Paper>
    </section>
  );
};

const OsDailyReportView = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const ownTechnicianId = user?.IDTecnicoOS;
  const [date, setDate] = useState(today);
  const [technicians, setTechnicians] = useState([]);
  const [technicianId, setTechnicianId] = useState("");
  const [techniciansLoading, setTechniciansLoading] = useState(isAdmin);
  const [techniciansError, setTechniciansError] = useState("");
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isAdmin) return undefined;
    let active = true;
    setTechniciansLoading(true);
    setTechniciansError("");
    api.get("/os/tecnicos", { params: { per_page: 200 } })
      .then(({ data: result }) => {
        if (!active) return;
        const items = result.data || [];
        setTechnicians(items);
        setTechnicianId(previous => items.some(item => String(item.id) === previous)
          ? previous : String((items.find(item => String(item.id) === String(ownTechnicianId)) || items[0])?.id || ""));
      })
      .catch(err => { if (active) setTechniciansError(message(err)); })
      .finally(() => { if (active) setTechniciansLoading(false); });
    return () => { active = false; };
  }, [isAdmin, ownTechnicianId, reload]);

  useEffect(() => {
    setData(null);
    setError("");
    if (!validDate(date) || (isAdmin ? !technicianId : !ownTechnicianId)) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    api.get("/dashboard/os-daily-report", { params: {
      date, page,
      ...(isAdmin ? { technician_id: technicianId } : {}),
      ...(reload ? { refresh: 1 } : {})
    } })
      .then(response => { if (active) setData(response.data); })
      .catch(err => { if (active) setError(message(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [date, page, isAdmin, technicianId, ownTechnicianId, reload]);

  const configured = isAdmin ? Boolean(technicianId) : Boolean(ownTechnicianId);
  const visibleData = data && data.date === date &&
    String(data.technician.id) === String(isAdmin ? technicianId : ownTechnicianId) ? data : null;
  const orders = visibleData?.orders || [];

  return (
    <div>
      <Typography variant="h6">Relatório diário de OS</Typography>
      <Typography variant="body2" color="textSecondary">
        OS do técnico criadas ou com andamento na data escolhida. Os status e a quantidade de fechadas refletem a situação atual.
      </Typography>
      <div className={classes.toolbar}>
        <TextField
          id="os-daily-report-date" type="date" label="Data do relatório" variant="outlined" size="small"
          className={classes.field} value={date} InputLabelProps={{ shrink: true }}
          onChange={event => { setDate(event.target.value); setPage(1); }}
          error={!validDate(date)} helperText={!validDate(date) ? "Informe uma data válida." : undefined}
        />
        {isAdmin && (
          <FormControl variant="outlined" size="small" className={classes.field} disabled={techniciansLoading}>
            <InputLabel id="daily-report-technician-label">Técnico</InputLabel>
            <Select labelId="daily-report-technician-label" label="Técnico" value={technicianId}
              onChange={event => { setTechnicianId(event.target.value); setPage(1); }}>
              {technicians.map(technician => <MenuItem key={technician.id} value={String(technician.id)}>{technician.nome}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <Button variant="outlined" color="primary" startIcon={<RefreshIcon />}
          onClick={() => setReload(value => value + 1)} disabled={loading || techniciansLoading || !validDate(date)}>
          Reconsultar
        </Button>
      </div>
      {techniciansError ? <Alert severity="error">{techniciansError}</Alert> :
        !configured && !techniciansLoading ? <Alert severity="info">{isAdmin
          ? "Nenhum técnico disponível para consulta."
          : "Seu usuário precisa estar vinculado a um técnico de OS. Solicite o vínculo ao administrador."}</Alert> :
        loading || techniciansLoading ? <div role="status" aria-label="Carregando relatório"><Skeleton variant="rect" height={70} /><Box mt={2}><Skeleton variant="rect" height={220} /></Box></div> :
        error ? <Alert severity="error">{error}</Alert> : visibleData ? (
          <>
            <Typography variant="subtitle2" gutterBottom>{visibleData.technician.name} · {dateLabel(visibleData.date)}</Typography>
            <Paper variant="outlined" className={classes.summary} aria-label="Resumo do dia">
              <span><strong>{visibleData.summary.total}</strong> OS no dia</span>
              <span><strong>{visibleData.summary.attention}</strong> requerem atenção</span>
              <span><strong>{visibleData.summary.closed}</strong> fechadas</span>
            </Paper>
            {!orders.length ? <Paper variant="outlined" className={classes.empty}>
              <Typography>Nenhuma OS criada ou com andamento nesta data para este técnico.</Typography>
              <Typography variant="body2" color="textSecondary">Selecione outra data para consultar o histórico.</Typography>
            </Paper> : <>
              <OrderSection title="Requerem atenção" orders={orders.filter(order => order.needs_attention)} attention />
              <OrderSection title="Demais OS do dia" orders={orders.filter(order => !order.needs_attention)} />
            </>}
            {visibleData.pagination.last_page > 1 && <div className={classes.pagination}>
              <Pagination count={visibleData.pagination.last_page} page={visibleData.pagination.page}
                onChange={(_, nextPage) => setPage(nextPage)} color="primary" />
            </div>}
          </>
        ) : null}
    </div>
  );
};

export default OsDailyReportView;
